import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { initializeApp, FirebaseApp } from 'firebase/app';
import {
  initializeAuth,
  Auth,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signOut,
  onAuthStateChanged,
  User,
  browserPopupRedirectResolver,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  browserSessionPersistence
} from 'firebase/auth';
import { loadConfig } from '../services/config';

/**
 * Impersonation context lifted from the Firebase ID token's
 * developer-claims. Set when the operator started a "Sign in as
 * customer" session from the Customer Hub. The browser carries
 * these claims naturally as long as Firebase keeps re-issuing the
 * ID token (~1h); after the token refreshes we re-read this block
 * and surface a "session ending" cue if the claim has dropped.
 */
export interface ImpersonationClaims {
  impersonatedBy: string;
  impersonatedByTag: string | null;
  impersonatedByEmail: string | null;
  targetCustomerId: string;
  targetCustomerTag: string | null;
  startedAt: number | null;
  expiresAt: number | null;
  reason: string | null;
}

interface UserPermissions {
  isAdmin: boolean;
  isPlatformAdmin: boolean;
  customerId: string;
  customerTag: string;
  userType?: string;
  permissions?: string[];
  emailVerified?: boolean;
  hasActiveSubscription?: boolean;
  subscriptionStatus?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
  isAdmin: boolean;
  isPlatformAdmin: boolean;
  permissions: UserPermissions | null;
  hasPermission: (permission: string) => boolean;
  isPublicMode: boolean;
  setIsPublicMode: (value: boolean) => void;
  getEffectiveCustomerId: () => string;
  getEffectiveCustomerTag: () => string;
  hasActiveSubscription: boolean;
  subscriptionStatus: string | null;
  refreshSubscriptionStatus: () => Promise<void>;
  /** True when the active Firebase session is an admin-impersonation
   *  session (developer claims carry `impersonated_by`). Read from
   *  `getIdTokenResult()` whenever the auth state changes. */
  isImpersonating: boolean;
  impersonationClaims: ImpersonationClaims | null;
  /** Sign the impersonation custom token in. Notifies the backend
   *  for audit and then calls Firebase `signInWithCustomToken`. */
  signInWithImpersonationToken: (customToken: string) => Promise<void>;
  /** End the impersonation session: tells the backend (audit log)
   *  and signs out, returning the operator to the login screen. */
  endImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [auth, setAuth] = useState<Auth | null>(null);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const authFailureCountRef = useRef(0);
  const [isPublicMode, setIsPublicMode] = useState(() => {
    // Initialize from localStorage
    const savedMode = localStorage.getItem('isPublicMode');
    return savedMode === 'true';
  });
  // Impersonation state. Re-read on every auth state change.
  const [impersonationClaims, setImpersonationClaims] =
    useState<ImpersonationClaims | null>(null);
  
  // Load cached permissions on mount
  useEffect(() => {
    const cachedPermissions = localStorage.getItem('userPermissions');
    if (cachedPermissions) {
      try {
        const parsed = JSON.parse(cachedPermissions);
        setPermissions(parsed);
        setIsAdmin(parsed.isAdmin || false);
        setIsPlatformAdmin(parsed.isPlatformAdmin || false);
      } catch (e) {

      }
    }
  }, []);

  // Persist isPublicMode changes to localStorage
  useEffect(() => {
    localStorage.setItem('isPublicMode', isPublicMode.toString());
  }, [isPublicMode]);

  useEffect(() => {
    let unsubscribeAuth: (() => void) | null = null;

    // Initialize Firebase with config from environment or API
    const initializeFirebase = async () => {
      try {
        // Load configuration first
        const config = await loadConfig();
        
        let firebaseConfig;
        
        // Check if we have Firebase config in the loaded config (for local dev)
        if ((config as any).firebaseConfig) {
          firebaseConfig = (config as any).firebaseConfig;
        } else {
          // Fetch shared Firebase config from API
          const response = await fetch(`${config.apiBaseUrl}/api/v2/customers/firebase-config/shared`);
          if (!response.ok) {
            throw new Error('Failed to fetch shared Firebase configuration');
          }
          firebaseConfig = await response.json();
        }
        
        // Initialize Firebase
        const app: FirebaseApp = initializeApp(firebaseConfig);
        // Tab-shared localStorage first, with fallbacks for contexts that block it. The order is
        // set here rather than with setPersistence after getAuth: getAuth starts with IndexedDB,
        // so every page load moved the session out of localStorage and back, and other open tabs
        // saw the removal as a sign-out and went to the login page.
        const authInstance = initializeAuth(app, {
          persistence: [browserLocalPersistence, indexedDBLocalPersistence, browserSessionPersistence],
          popupRedirectResolver: browserPopupRedirectResolver,
        });
        setAuth(authInstance);

        
        // Set up auth state listener
        unsubscribeAuth = onAuthStateChanged(authInstance, async (user) => {
          if (user) {

            // Set user immediately to prevent redirect while validating
            setUser(user);

            // Read impersonation developer-claims from the active
            // ID token. These are present iff the operator signed in
            // via a custom token minted by the Customer Hub. They
            // expire naturally with the Firebase ID token (~1h).
            try {
              const tokenResult = await user.getIdTokenResult();
              const c: any = tokenResult.claims || {};
              if (c.impersonated_by) {
                setImpersonationClaims({
                  impersonatedBy: String(c.impersonated_by),
                  impersonatedByTag: c.impersonated_by_tag || null,
                  impersonatedByEmail: c.impersonated_by_email || null,
                  targetCustomerId: c.impersonation_target || '',
                  targetCustomerTag: c.impersonation_target_tag || null,
                  startedAt: c.impersonation_started_at
                    ? Number(c.impersonation_started_at)
                    : null,
                  expiresAt: c.impersonation_expires_at
                    ? Number(c.impersonation_expires_at)
                    : null,
                  reason: c.impersonation_reason || null,
                });
              } else {
                setImpersonationClaims(null);
              }
            } catch (_claimsErr) {
              setImpersonationClaims(null);
            }
            
            // With shared Firebase, we need to validate customer access via API
            try {
              const token = await user.getIdToken();

              
              // Retry validation for new signups
              let retries = 0;
              let validateResponse;
              
              while (retries < 3) {
                validateResponse = await fetch(`${config.apiBaseUrl}/api/v2/customers/current`, {
                  headers: {
                    'Authorization': `Bearer ${token}`
                  }
                });
                

                
                if (validateResponse.ok || validateResponse.status !== 404) {
                  break;
                }
                
                // If 404 (user not found yet), wait and retry (for new signups)
                if (validateResponse.status === 404 && retries < 2) {

                  await new Promise(resolve => setTimeout(resolve, 1000));
                  retries++;
                  // Get fresh token on retry
                  const freshToken = await user.getIdToken(true);
                  validateResponse = await fetch(`${config.apiBaseUrl}/api/v2/customers/current`, {
                    headers: {
                      'Authorization': `Bearer ${freshToken}`
                    }
                  });
                  if (validateResponse.ok) break;
                } else {
                  break;
                }
              }
              
              if (validateResponse && !validateResponse.ok && (validateResponse.status === 401 || validateResponse.status === 403)) {
                // One forced refresh before considering this an auth failure.
                try {
                  const refreshedToken = await user.getIdToken(true);
                  const retryResponse = await fetch(`${config.apiBaseUrl}/api/v2/customers/current`, {
                    headers: {
                      'Authorization': `Bearer ${refreshedToken}`
                    }
                  });
                  validateResponse = retryResponse;
                } catch (_refreshErr) {
                  // Keep original response if refresh/retry fails.
                }
              }

              if (validateResponse && validateResponse.ok) {
                const response = await validateResponse.json();
                authFailureCountRef.current = 0;
                setError(null);

                const customerData = response.data;
                // User already set above
                setIsAdmin(customerData.user.is_admin || false);
                // Platform admin is determined by user_type being 'admin'
                const isPlatformAdminUser = customerData.user_type === 'admin';
                setIsPlatformAdmin(isPlatformAdminUser);
                
                // Check for active subscription OR if user has credits
                // Users with credits but no Stripe subscription (free tier) should still have access
                const hasSubscription = customerData.subscription && (
                  customerData.subscription.status === 'active' || 
                  customerData.subscription.status === 'trialing' ||
                  (customerData.subscription.total_credits && customerData.subscription.total_credits > 0)
                );
                setHasActiveSubscription(hasSubscription || false);
                setSubscriptionStatus(customerData.subscription?.status || null);
                
                // Clear public mode if user is not an admin
                if (!customerData.user.is_admin && !isPlatformAdminUser) {
                  setIsPublicMode(false);
                  localStorage.removeItem('isPublicMode');
                }
                const userPermissions = {
                  isAdmin: customerData.user.is_admin || false,
                  isPlatformAdmin: isPlatformAdminUser,
                  customerId: customerData.customer_id || '',
                  customerTag: customerData.customer_tag || config.customerTag,
                  userType: customerData.user_type || 'customer',
                  permissions: customerData.user.permissions || [],
                  emailVerified: customerData.user.email_verified || false,
                  hasActiveSubscription: hasSubscription || false,
                  subscriptionStatus: customerData.subscription?.status || null
                };
                setPermissions(userPermissions);
                // Cache permissions
                localStorage.setItem('userPermissions', JSON.stringify(userPermissions));
              } else {
                const status = validateResponse?.status || 0;
                const isAuthFailure = status === 401 || status === 403;

                if (isAuthFailure) {
                  authFailureCountRef.current += 1;
                  if (authFailureCountRef.current >= 3) {
                    // Escalate only after repeated definitive auth failures.
                    setError('Session expired or access changed. Please sign in again.');
                    await signOut(authInstance);
                    setUser(null);
                    setPermissions(null);
                    localStorage.removeItem('userPermissions');
                  } else {
                    // Keep the session for transient backend/auth races between surfaces.
                    setError('Auth check failed temporarily. Keeping your session.');
                  }
                } else {
                  // Keep the local session for transient failures (5xx/timeouts/gateway issues).
                  setError('Temporary validation issue. Keeping session and retrying.');
                }
              }
            } catch (err) {

              // Check if it's a network error
              if ((err as any)?.message?.includes('Failed to fetch') || (err as any)?.code === 'ERR_NETWORK') {

                setError('Network error during validation. Keeping your session.');
              } else {
                // Do not aggressively sign out on transient/unknown validation errors.
                // Preserve retained session and let Firebase continue token refresh.
                setError('Failed to validate access right now. Keeping your session.');
              }
            }
          } else {
            authFailureCountRef.current = 0;
            setUser(null);
            setIsAdmin(false);
            setIsPlatformAdmin(false);
            setPermissions(null);
            setHasActiveSubscription(false);
            setSubscriptionStatus(null);
            setIsPublicMode(false);
            setImpersonationClaims(null);
            localStorage.removeItem('isPublicMode');
          }
          setLoading(false);
        });
      } catch (err) {

        setError('Failed to initialize authentication');
        setLoading(false);
      }
    };
    
    initializeFirebase();

    return () => {
      if (unsubscribeAuth) {
        unsubscribeAuth();
      }
    };
  }, []);

  // Keep Firebase tokens warm across tab switching/embedded usage.
  useEffect(() => {
    if (!auth) return;

    const refreshToken = async (force: boolean) => {
      try {
        const current = auth.currentUser;
        if (!current) return;
        await current.getIdToken(force);
      } catch (_err) {
        // Best effort only.
      }
    };

    // Not forced: getIdToken refreshes only when the token is near expiry, so switching tabs
    // doesn't rewrite the shared session in every other open tab.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void refreshToken(false);
      }
    };

    const intervalId = window.setInterval(() => {
      void refreshToken(false);
    }, 10 * 60 * 1000); // 10 minutes

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [auth]);

  const login = async (email: string, password: string) => {
    if (!auth) {
      throw new Error('Authentication not initialized');
    }
    
    try {
      setError(null);
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err: any) {

      setError(err.message || 'Failed to log in');
      throw err;
    }
  };

  const logout = async () => {
    if (!auth) {
      throw new Error('Authentication not initialized');
    }
    
    try {
      await signOut(auth);
      // Cleared here too (the auth listener also does it) so the login page, which forwards
      // signed-in users, never sees the old user right after logging out.
      setUser(null);
      // Clear cached permissions and public mode
      localStorage.removeItem('userPermissions');
      localStorage.removeItem('isPublicMode');
      setIsPublicMode(false);
    } catch (err: any) {

      setError(err.message || 'Failed to log out');
      throw err;
    }
  };

  const getIdToken = async (): Promise<string | null> => {
    if (!user) return null;
    
    try {
      return await user.getIdToken();
    } catch (err) {

      return null;
    }
  };

  const hasPermission = (permission: string): boolean => {
    if (!permissions) return false;
    
    // Platform admins have all permissions
    if (permissions.isPlatformAdmin) return true;
    
    // Check specific permission
    return permissions.permissions?.includes(permission) || false;
  };

  const getEffectiveCustomerId = (): string => {
    if (isPublicMode && (isAdmin || isPlatformAdmin)) {
      return 'public';
    }
    return permissions?.customerId || '';
  };

  const getEffectiveCustomerTag = (): string => {
    if (isPublicMode && (isAdmin || isPlatformAdmin)) {
      return 'public';
    }
    return permissions?.customerTag || '';
  };

  const refreshSubscriptionStatus = async () => {
    if (!auth || !user) {

      return;
    }

    try {
      const token = await user.getIdToken();
      const config = await loadConfig();
      const response = await fetch(`${config.apiBaseUrl}/api/v2/customers/current`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const customerData = data.data;
        setHasActiveSubscription(customerData.subscription && 
          (customerData.subscription.status === 'active' || 
           customerData.subscription.status === 'trialing'));
        setSubscriptionStatus(customerData.subscription?.status || null);
        // Cache the updated permissions
        const userPermissions = {
          isAdmin: customerData.user.is_admin || false,
          isPlatformAdmin: customerData.user_type === 'admin',
          customerId: customerData.customer_id || '',
          customerTag: customerData.customer_tag || config.customerTag,
          userType: customerData.user_type || 'customer',
          permissions: customerData.user.permissions || [],
          emailVerified: customerData.user.email_verified || false,
          hasActiveSubscription: customerData.subscription && 
            (customerData.subscription.status === 'active' || 
             customerData.subscription.status === 'trialing' ||
             customerData.subscription.status === 'past_due'),
          subscriptionStatus: customerData.subscription?.status || null
        };
        setPermissions(userPermissions);
        localStorage.setItem('userPermissions', JSON.stringify(userPermissions));
      } else {

      }
    } catch (err) {

    }
  };

  // -------- Impersonation -----------------------------------------
  // The Customer Hub mints a Firebase custom token; we exchange it
  // here. `signInWithCustomToken` triggers `onAuthStateChanged`,
  // which re-runs the impersonation-claim read above — so by the
  // time this method's promise resolves, `impersonationClaims` is
  // already populated for downstream consumers.
  const signInWithImpersonationToken = async (customToken: string): Promise<void> => {
    if (!auth) throw new Error('Firebase not yet initialized');
    // Sign out the current admin session first so the new sign-in
    // is clean. We do NOT clear localStorage manually here: the
    // auth state listener handles state reset on signOut.
    try { await signOut(auth); } catch (_e) { /* ignore */ }
    await signInWithCustomToken(auth, customToken);
  };

  const endImpersonation = async (): Promise<void> => {
    if (!auth) return;
    // Best-effort backend audit before signing out. We don't want
    // a slow / broken backend call to block the user from getting
    // out of the impersonation session, so it's fire-and-forget
    // with a short timeout.
    try {
      const token = user ? await user.getIdToken() : null;
      if (token) {
        const cfg = await loadConfig();
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), 5000);
        try {
          await fetch(
            `${cfg.apiBaseUrl}/api/v2/admin/customer-hub/end-impersonation`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              signal: ctrl.signal,
            }
          );
        } catch (_e) {
          // Audit endpoint failure does not block sign-out.
        } finally {
          clearTimeout(timer);
        }
      }
    } catch (_e) {
      // Token / config errors don't block sign-out.
    }
    await signOut(auth);
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    getIdToken,
    isAdmin,
    isPlatformAdmin,
    permissions,
    hasPermission,
    isPublicMode,
    setIsPublicMode,
    getEffectiveCustomerId,
    getEffectiveCustomerTag,
    hasActiveSubscription,
    subscriptionStatus,
    refreshSubscriptionStatus,
    isImpersonating: !!impersonationClaims,
    impersonationClaims,
    signInWithImpersonationToken,
    endImpersonation,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
